import { Injectable, signal } from '@angular/core';
import { PlanningDataSource } from '@digiwall/goliath-documents/proxy';

/** Which card context a visibility choice applies to. */
export type PlanningCardContext = 'planned' | 'backlog';

const STORAGE_PREFIX = 'planning-card-fields:';

/**
 * Per-user visibility of planning-card body fields, split by data source and by board context
 * (planned vs backlog). Every field is visible by default; only the user's explicit hides/shows are
 * stored, as overrides keyed `${dataSource}:${context}:${fieldKey}`. Overrides are persisted in
 * localStorage, scoped per board via the routed `configId`, so choices survive reloads but stay local
 * to the browser. Provided at the board component level so state lives with the open board.
 */
@Injectable()
export class PlanningCardFieldVisibilityStore {
  private storageKey = '';
  private readonly overrides = signal<Record<string, boolean>>({});

  /** Binds the store to a board and loads that board's persisted overrides. */
  init(configId: string): void {
    this.storageKey = `${STORAGE_PREFIX}${configId}`;
    this.overrides.set(this.readStored());
  }

  isVisible(dataSource: PlanningDataSource, context: PlanningCardContext, fieldKey: string): boolean {
    return this.overrides()[this.key(dataSource, context, fieldKey)] ?? true;
  }

  setVisible(
    dataSource: PlanningDataSource,
    context: PlanningCardContext,
    fieldKey: string,
    value: boolean,
  ): void {
    this.overrides.update(current => {
      const next = { ...current, [this.key(dataSource, context, fieldKey)]: value };
      this.persist(next);
      return next;
    });
  }

  private key(dataSource: PlanningDataSource, context: PlanningCardContext, fieldKey: string): string {
    return `${dataSource}:${context}:${fieldKey}`;
  }

  private readStored(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  }

  private persist(state: Record<string, boolean>): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      // Best-effort convenience — ignore quota/serialization failures.
    }
  }
}
